import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Search } from 'lucide-react';
import { Button } from '../ui/Button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/Sheet';
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@/types';
import { resolveRouteAccessPolicy } from '@/routes/accessPolicy';

type NavLinkItem = {
  name: string;
  href: string;
  requiredRoles?: UserRole[];
  isAnchor?: boolean;
};

const marketingLinks: NavLinkItem[] = [
  { name: 'Features', href: '/features' },
  { name: 'For Chambers', href: '/for-chambers' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Docs', href: '/docs' },
  { name: 'Verify CO', href: '/verify-co' },
  { name: 'Community', href: '/community' },
];

const signupLinks: NavLinkItem[] = [
  { name: 'Pricing', href: '/pricing' },
  { name: 'Docs', href: '/docs' },
  { name: 'Contact', href: '/contact', isAnchor: false },
];

const platformLinks: NavLinkItem[] = [
  { name: 'Dashboard', href: '/dashboard' },
  { name: 'Directory', href: '/directory' },
  { name: 'Members', href: '/members' },
  { name: 'Events', href: '/events' },
  { name: 'Advocacy', href: '/advocacy' },
  { name: 'Trade', href: '/trade' },
  { name: 'Marketplace', href: '/marketplace' },
  { name: 'Billing', href: '/billing', requiredRoles: ['CHAMBER_CEO', 'MEMBERSHIP_MANAGER', 'IT_ADMIN'] },
  { name: 'Notifications', href: '/notifications' },
  { name: 'Settings', href: '/settings', requiredRoles: ['IT_ADMIN'] },
];

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, hasRole } = useAuth();

  const policy = resolveRouteAccessPolicy(location.pathname);
  const isProtectedContext = policy.requiresAuth;
  const isSignupPage = location.pathname === '/signup';

  const navLinks = useMemo(() => {
    if (isProtectedContext && isAuthenticated) {
      return platformLinks.filter((link) => !link.requiredRoles || hasRole(link.requiredRoles));
    }

    if (isSignupPage && !isAuthenticated) {
      return signupLinks;
    }

    return marketingLinks;
  }, [hasRole, isAuthenticated, isProtectedContext, isSignupPage]);

  const showPlatformActions = isProtectedContext && isAuthenticated;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:p-4 focus:bg-background focus:text-foreground focus:rounded-md">
        
        Skip to main content
      </a>
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="header-link-logo">
            
            <img 
              src="/OpenChambers-Logo.png"
              alt="OpenChambers Logo"
              className="h-12 w-auto"
              loading="lazy"
            />
          </Link>
        </div>

        {/* Desktop Nav */}
        <nav
          className="hidden md:flex items-center gap-6"
          aria-label="Main navigation"
          data-testid="nav-menu-main">
          
          {navLinks.map((link) =>
          link.isAnchor ?
            <a
              key={link.name}
              href={link.href}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>
              {link.name}
            </a> :
            <Link
              key={link.name}
              to={link.href}
              className={`text-sm font-medium transition-colors hover:text-foreground ${location.pathname === link.href ? 'text-foreground' : 'text-muted-foreground'}`}
              data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>
              {link.name}
            </Link>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          {showPlatformActions ?
          <>
              <Link to="/search">
                <Button
                variant="ghost"
                size="icon"
                aria-label="Search"
                data-testid="header-btn-search">
                
                  <Search className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/">
                <Button
                variant="outline"
                size="sm"
                data-testid="header-btn-backToSite">
                
                  Back to Site
                </Button>
              </Link>
            </> :
            isSignupPage ?
            <Link to="/">
              <Button
                variant="outline"
                size="sm"
                data-testid="header-btn-signupBackToSite"
              >
                Back to Site
              </Button>
            </Link> :
          <>
              <Link
              to="/dashboard"
              className="text-sm font-medium text-muted-foreground hover:text-foreground"
              data-testid="header-link-signin">
              
                Sign In
              </Link>
              <Link to="/dashboard">
                <Button
                className="bg-oc-emerald hover:bg-oc-emerald-light text-white"
                data-testid="header-btn-getStarted">
                
                  Get Started
                </Button>
              </Link>
            </>
          }
        </div>
        {/* End desktop right actions */}

        {/* Mobile Nav */}
        <div className="md:hidden">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open menu"
                data-testid="nav-btn-mobileToggle">
                
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <nav
                className="flex flex-col gap-4 mt-8"
                aria-label="Mobile navigation">
                
                {navLinks.map((link) =>
                link.isAnchor ?
                  <a
                    key={link.name}
                    href={link.href}
                    className="text-lg font-medium text-muted-foreground"
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    {link.name}
                  </a> :
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`text-lg font-medium ${location.pathname === link.href ? 'text-foreground' : 'text-muted-foreground'}`}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    {link.name}
                  </Link>
                )}
                <div className="flex flex-col gap-4 mt-8 pt-8 border-t border-border">
                  {showPlatformActions ?
                  <Link to="/" onClick={() => setIsOpen(false)}>
                      <Button
                      variant="outline"
                      className="w-full"
                      data-testid="mobile-header-btn-backToSite">
                      
                        Back to Site
                      </Button>
                    </Link> :
                    isSignupPage ?
                    <Link to="/" onClick={() => setIsOpen(false)}>
                      <Button
                        variant="outline"
                        className="w-full"
                        data-testid="mobile-header-btn-signupBackToSite"
                      >
                        Back to Site
                      </Button>
                    </Link> :

                  <>
                      <Link
                      to="/dashboard"
                      className="text-lg font-medium text-foreground"
                      onClick={() => setIsOpen(false)}
                      data-testid="mobile-header-link-signin">
                      
                        Sign In
                      </Link>
                      <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                        <Button
                        className="bg-oc-emerald hover:bg-oc-emerald-light text-white w-full"
                        data-testid="mobile-header-btn-getStarted">
                        
                          Get Started
                        </Button>
                      </Link>
                    </>
                  }
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>);

}